/**
 * 지원 모드 클라이언트 상태
 * ---------------------------------------------------------------------------
 * 플랫폼 운영자가 고객사 데이터를 다루는 동안의 상태를 한곳에 모은다.
 *
 * 왜 React 상태가 아니라 모듈 상태인가
 *   api-client 의 인터셉터가 이 값을 읽어야 한다. 토큰이 만료됐을 때
 *   일반 세션(/auth/refresh)이 아니라 지원 세션(/support/refresh)으로
 *   갱신해야 하기 때문이다. 이걸 틀리면 운영자가 **모르는 사이에 자기
 *   기업 컨텍스트로 돌아간 채 고객사 화면을 계속 보게 된다.**
 *
 * 토큰 자체는 여기 두지 않는다. Access Token 은 api-client 메모리에,
 * Refresh 토큰은 HttpOnly 쿠키에 있다.
 */

export interface SupportSession {
  supportSessionId: string;
  /** 작업 대상 기업 */
  tenantId: string;
  companyName: string;
  /** 자동 종료 시각 (ISO) */
  expiresAt: string;
}

/** 서버가 함께 내려주는 표시용 쿠키. 값이 아니라 존재 여부만 의미가 있다. */
const HINT_COOKIE = 'wnh_support';

let current: SupportSession | null = null;
const listeners = new Set<(s: SupportSession | null) => void>();

export function getSupportSession(): SupportSession | null {
  return current;
}

export function setSupportSession(next: SupportSession | null): void {
  current = next;
  for (const listener of listeners) listener(next);
}

export function subscribeSupportSession(fn: (s: SupportSession | null) => void): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * 새로고침 직후 "지원 모드 복구를 시도할 가치가 있는지" 판단한다.
 * 실제 인증은 HttpOnly 쿠키로 서버가 다시 검증한다.
 */
export function hasSupportHint(): boolean {
  return document.cookie.split(';').some((c) => c.trim().startsWith(`${HINT_COOKIE}=`));
}

/** 남은 시간을 사람이 읽는 문구로. 만료됐으면 null. */
export function remainingText(expiresAt: string): string | null {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (Number.isNaN(ms) || ms <= 0) return null;

  const minutes = Math.floor(ms / 60_000);
  if (minutes < 60) return `${minutes}분`;
  return `${Math.floor(minutes / 60)}시간 ${minutes % 60}분`;
}
